// src/components/Dashboard.js
import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  useUser,
  UserButton,
  useOrganization,
  useOrganizationList,
  OrganizationSwitcher,
} from "@clerk/clerk-react";
import {
  Home,
  BookOpen,
  Settings,
  MessageCircle,
 Contact,
  BarChart3,
  Menu,
  X,
  Activity,
  Building2,
  Users,
  CreditCard
} from "lucide-react";
import toast from "react-hot-toast";
// Import context
import {
  useOrganizationContext,  CreateFirstOrganization,
  SelectOrganization,
} from "../contexts/OrganizationContext";

// Import your custom create-org form
import { CustomCreateOrganization } from "./CustomCreateOrganization";

// Import tab components
import Overview from "./tabs/Overview";
import AboutSection from "./tabs/AboutSection";
import KnowledgeVault from "./tabs/KnowledgeVault";
import WidgetManager from "./tabs/WidgetManager";
import AnswerOptimization from "./tabs/AnswerOptimization";
import SupportQueries from "./tabs/SupportQueries";
import AITrafficAnalytics from "./tabs/AITrafficAnalytics";
import Billing from "./tabs/Billing";
import NotFound from "./NotFound";

import ApiService from "../services/ApiService";

const navigation = [
  { name: "Overview", href: "/", icon: Home },
  { name: "About", href: "/about", icon: Building2 },
  { name: "Knowledge Vault", href: "/knowledge", icon: BookOpen },
  { name: "Widget Manager", href: "/widgets", icon: Settings },
  { name: "FAQ's Answers", href: "/optimization", icon: MessageCircle },
  { name: "Support Queries", href: "/support", icon: Contact },
  { name: "AI Traffic Analytics", href: "/analytics", icon: BarChart3 },
  { name: "Billing", href: "/billing", icon: CreditCard },
];




// const navigation = [
//   { name: "Overview", href: "/", icon: Home },
//   { name: "About", href: "/about", icon: Building2 },
//   { name: "Knowledge Vault", href: "/knowledge", icon: BookOpen },
//   { name: "Widget Manager", href: "/widgets", icon: Settings },
//   { name: "Answer Optimization", href: "/optimization", icon: MessageCircle },
//   { name: "Support Queries", href: "/support", icon: LifeBuoy },
//   { name: "AI Traffic Analytics", href: "/analytics", icon: BarChart3 },
// ];

function Dashboard() {
  return <DashboardContent />;
}

function DashboardContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [healthStatus, setHealthStatus] = useState(null);
  const { user } = useUser();
  const { organization, isLoaded } = useOrganization();
  const { organizationList } = useOrganizationList();
  const { orgReady, isInitializing } = useOrganizationContext();

  // Handle redirect after sign in (set from LandingPage before opening Clerk modal)
  useEffect(() => {
    try {
      const redirectAfter = localStorage.getItem("redirectAfterSignIn");
      if (redirectAfter) {
        localStorage.removeItem("redirectAfterSignIn");
        navigate(redirectAfter, { replace: true });
      }
    } catch (err) {
      console.warn("redirectAfterSignIn check failed in Dashboard", err);
    }
  }, [navigate]);

  // Show first-time plan selection prompt on Overview once per organization
  useEffect(() => {
    if (!orgReady || !organization) return;
    // Only on Overview route
    if (location.pathname !== "/") return;

    const key = `planPromptShown_${organization.id}`;
    if (localStorage.getItem(key) === "1") return;

    const tId = toast((t) => (
      <div className="flex items-start gap-3">
        <div className="text-sm">
          <div className="font-semibold text-gray-900">Welcome! Finish setup</div>
          <div className="text-gray-700">Choose a plan to activate your free trial.</div>
        </div>
        <button
          onClick={() => {
            localStorage.setItem(key, "1");
            toast.dismiss(t.id);
            navigate("/billing");
          }}
          className="ml-4 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          Choose Plan
        </button>
      </div>
    ), { duration: 8000 });

    // Mark as shown so it doesn't reappear on refresh
    localStorage.setItem(key, "1");

    return () => {
      if (tId) toast.dismiss(tId);
    };
  }, [orgReady, organization, location.pathname, navigate]);

  useEffect(() => {
    const checkHealth = async () => {
      if (!orgReady) return;
      try {
        const health = await ApiService.healthCheck();
        setHealthStatus(health);
        if (health.status !== "healthy") {
          toast.error("Some services are unavailable");
        }
      } catch (error) {
        console.error("Health check failed:", error);
        toast.error("Unable to connect to backend services");
      }
    };
    checkHealth();
  }, [orgReady]);

  const urlParams = new URLSearchParams(window.location.search);
  const isAutomated = urlParams.get('automated') === 'true';

  if (isInitializing || !isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (organizationList?.length === 0 && !isAutomated) {
    return <CreateFirstOrganization />;
  }

  if (!organization && !isAutomated) {
    return <SelectOrganization />;
  }

  if (!orgReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Setting up organization...</p>
        </div>
      </div>
    );
  }

  return <MainDashboard />;
}

function MainDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key
  const { user } = useUser();

  // Open modal when hash is "#create-org"
  useEffect(() => {
    setCreateOrgOpen(location.hash === "#create-org");
  }, [location.hash]);
  const closeCreateOrg = () => {
    // remove hash without pushing history
    navigate(location.pathname, { replace: true });
    // Trigger a refresh of child components
    setRefreshKey((prev) => prev + 1);
  };

  // Validate routes
  const validRoutes = [
    "/",
    "/about",
    "/knowledge",
    "/widgets",
    "/optimization",
    "/support",
    "/analytics",
    "/billing",
  ];
  if (!validRoutes.includes(location.pathname)) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-2xl">
            <button
              className="absolute top-0 right-0 m-4"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-gray-700" />
            </button>
            <SidebarContent setSidebarOpen={setSidebarOpen} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-30">
        <div className="flex-1 flex flex-col bg-white border-r border-gray-200 shadow-sm">
          <SidebarContent setSidebarOpen={() => {}} />
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top nav */}
        <header className="sticky top-0  bg-white border-b border-gray-200 shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Mobile menu */}
              <div className="md:hidden">
                <button
                  className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>

              {/* Org switcher */}
              <div className="flex-1 flex items-center">
                <OrganizationSwitcher
                  appearance={{
                    elements: {
                      organizationSwitcherTrigger:
                        "flex items-center space-x-2 px-4 py-2.5 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
                      organizationPreview: "flex items-center space-x-2",
                      organizationSwitcherPopoverCard:
                        "bg-white border border-gray-200 rounded-lg shadow-xl mt-2",
                      organizationSwitcherPopoverFooter:
                        "border-t border-gray-200 p-3",
                      organizationSwitcherPopoverActionButton:
                        "w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md",
                    },
                  }}
                  createOrganizationMode="navigation"
                  createOrganizationUrl="#create-org"
                  afterSelectOrganizationUrl="/"
                  hidePersonal
                />
              </div>

              {/* User menu */}
              <div className="flex items-center space-x-4">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName || "User"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.emailAddresses[0]?.emailAddress ||
                      "user@example.com"}
                  </p>
                </div>
                 <UserButton
                  afterSignOutUrl="/"
                  userProfileProps={{
                    appearance: {
                      elements: {
                        navbarButton: {
                          "&.cl-navbarButton__billing": {              //disabled the billing menu button
                            display: "none !important",
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </header>

        {/* Create Org Modal */}
        {createOrgOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black opacity-50"
              onClick={closeCreateOrg}
            />
            <div className="relative bg-white rounded-lg shadow-lg p-6 max-w-md w-full z-10">
              <button
                className="absolute top-3 right-3"
                onClick={closeCreateOrg}
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>{" "}              <CustomCreateOrganization
                isModal={false}
                onSuccess={(org) => {
                  // console.log("Organization created successfully:", org);
                  closeCreateOrg();
                  // Just refresh the component state, no reload needed
                  setRefreshKey((prev) => prev + 1);
                }}
              />
            </div>
          </div>
        )}

        {/* Main area */}
        <main className="flex-1 bg-gray-50 relative">
          <div className="p-6 max-w-7xl mx-auto">
            {" "}
            <Routes>
              <Route path="/" element={<Overview key={refreshKey} />} />
              <Route
                path="/about"
                element={<AboutSection key={refreshKey} />}
              />
              <Route
                path="/knowledge"
                element={<KnowledgeVault key={refreshKey} />}
              />
              <Route
                path="/widgets"
                element={<WidgetManager key={refreshKey} />}
              />
              <Route
                path="/optimization"
                element={<AnswerOptimization key={refreshKey} />}
              />
              <Route
                path="/support"
                element={<SupportQueries key={refreshKey} />}
              />
              <Route
                path="/analytics"
                element={<AITrafficAnalytics key={refreshKey} isMenuOpen={sidebarOpen} />}
              />
              <Route path="/billing" element={ <Billing key={refreshKey} /> } />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ setSidebarOpen }) {
  const location = useLocation();
  const { organization } = useOrganization();
  const { getCurrentRole, isAdmin } = useOrganizationContext();

  return (
    <>
      {/* Branding */}
      <div className="flex items-center px-6 py-5 border-b border-gray-200">
        <div className="flex items-center space-x-3 w-full">
          <img
            src="/favicon.png"
            alt="Uttik"
            className="h-10 w-10 md:h-12 md:w-12 object-contain rounded"
          />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Uttik</h1>
            <p className="text-xs text-gray-500 font-medium">AI Answer Engine</p>
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-4 py-6 space-y-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive
                  ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600"
                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon
                className={`mr-3 h-5 w-5 transition-colors ${
                  isActive
                    ? "text-blue-600"
                    : "text-gray-400 group-hover:text-gray-600"
                }`}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Org stats */}
      {organization && (
        <div className="px-4 py-4 border-t border-gray-200 bg-gray-50/50">
          <div className="flex items-start space-x-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-900">
                {organization.name}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {isAdmin() ? "Administrator" : "Member"} •{" "}
                {getCurrentRole().charAt(0).toUpperCase() +
                  getCurrentRole().slice(1)}
              </p>
              <div className="mt-2 flex items-center space-x-2">
                <Users className="h-3 w-3 text-blue-600" />
                <span className="text-xs text-blue-600">
                  {organization.membersCount || 1} member
                  {(organization.membersCount || 1) !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Dashboard;
